import React from 'react';
import styles from './User.module.scss';
import { useParams } from 'react-router-dom';
import Loading from '../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../Common/Error/Error';
import useApiData from '../../../../../hooks/useApiData';
import { DummyUserPortrait } from '../../../User/Team/Meetings/Meeting/UserPortrait/UserPortrait';
import Table from '../../../Common/Table/Table/Table';
import ScoreDisplay from '../../../Common/ScoreDisplay/ScoreDisplay';
import SaveIcon from '@mui/icons-material/Save';
import APIClient from '../../../../../util/APIClient';
import useAuth from '../../../../../hooks/useAuth';

export default function User() {
  const { groups } = useAuth();
  const [selectedGroups, setSelectedGroups] = React.useState([]);
  const { userId } = useParams();
  const {
    data: groupsData,
    isLoading: groupsIsLoading,
    error: groupsError,
  } = useApiData({
    path: `/administrator/groups`,
  });
  const {
    data: userData,
    isLoading: userIsLoading,
    error: userError,
    reload: userReload,
  } = useApiData({
    path: `/administrator/users/${userId}`,
    params: {
      includeTeams: true,
      includeGroups: true,
    },
  });

  const updateGroupMemberships = React.useCallback(async () => {
    await APIClient.patch(`/administrator/users/${userId}/groups`, {
      groupIds: selectedGroups,
    });
    userReload();
  }, [userData, selectedGroups]);

  if (userIsLoading) {
    return <Loading message="Fetching user..." />;
  }
  if (groupsIsLoading) {
    return <Loading message="Fetching groups..." />;
  }

  if (userError) {
    return getErrorComponentFromHttpError(userError);
  }
  if (groupsError) {
    return getErrorComponentFromHttpError(groupsError);
  }

  return (
    <div className={styles.user}>
      <div className={styles.title}>
        <DummyUserPortrait cognitoId={userData.cognitoId} firstName={userData.firstName} lastName={userData.lastName} />
        <h1>
          {userData.firstName} {userData.lastName}
        </h1>
      </div>
      <div className={styles.groups}>
        <Table
          rows={groupsData.map((g) => {
            return {
              ...g,
              displayLabel: g.label
                .replace(/([A-Z])/g, ' $1')
                .trim()
                .replace(/^./, (char) => char.toUpperCase())
                .replace(/ (a-z)/, (char) => ` ${char.toUpperCase()}`),
            };
          })}
          selectedActions={
            groups.includes('administrators')
              ? [
                  {
                    label: 'Update Group Memberships',
                    icon: <SaveIcon />,
                    handler: updateGroupMemberships,
                  },
                ]
              : undefined
          }
          allowSelect={groups.includes('administrators')}
          allowPagination={false}
          onSelected={groups.includes('administrators') ? (selected) => setSelectedGroups(selected) : undefined}
          defaultSelected={groups.includes('administrators') ? userData.groups.map((g) => g.id) : undefined}
          title="Groups memberships"
          columns={[
            {
              id: 'displayLabel',
              label: 'Label',
            },
          ]}
        />
      </div>
      <div className={styles.teams}>
        <Table
          allowPagination={false}
          rows={userData.teams.map((t) => ({
            ...t,
            performanceScoreTeam: (
              <ScoreDisplay
                subjectType="team"
                scoreType="performance"
                teamId={t.id}
                fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                toDate={new Date().toISOString().substring(0, 10)}
              />
            ),
            performanceScoreTeamMember: (
              <ScoreDisplay
                subjectType="teamMember"
                scoreType="performance"
                teamId={t.id}
                userId={userData.cognitoId}
                fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                toDate={new Date().toISOString().substring(0, 10)}
              />
            ),
            processAdoptionScoreTeam: (
              <ScoreDisplay
                subjectType="team"
                scoreType="processAdoption"
                teamId={t.id}
                fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                toDate={new Date().toISOString().substring(0, 10)}
              />
            ),
            processAdoptionScoreTeamMember: (
              <ScoreDisplay
                subjectType="teamMember"
                scoreType="processAdoption"
                teamId={t.id}
                userId={userData.cognitoId}
                fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                toDate={new Date().toISOString().substring(0, 10)}
              />
            ),
          }))}
          linkFunction={(id) => `/administrator/teams/${id}`}
          rowLinks={{
            label: {
              label: 'View Team',
              to: (id) => `/administrator/teams/${id}`,
            },
          }}
          title="Team memberships"
          headers={[
            {
              colSpan: 2,
              label: null,
            },
            {
              colSpan: 2,
              label: 'Performance Score (6 weeks)',
            },
            {
              colSpan: 2,
              label: 'Process Adoption Score (7 days)',
            },
          ]}
          columns={[
            {
              id: 'label',
              label: 'Label',
              numeric: false,
            },
            {
              id: 'isLead',
              label: 'Team Leader',
              boolean: true,
            },
            {
              id: 'performanceScoreTeam',
              label: 'Team',
              component: true,
            },
            {
              id: 'performanceScoreTeamMember',
              label: 'Team Member',
              component: true,
            },
            {
              id: 'processAdoptionScoreTeam',
              label: 'Team',
              component: true,
            },
            {
              id: 'processAdoptionScoreTeamMember',
              label: 'Team Member',
              component: true,
            },
          ]}
        />
      </div>
    </div>
  );
}
